import { capitalCase } from 'change-case';
import React, { useCallback } from 'react';
import ntc from '../../../../@vendor/ntc';
import { hexToRgb } from '../../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../../Legacy';
import { Component, ComponentType } from '../../../../concepts/objects';
import { ExportDimension, ExportDimensionType, ExportDimensionUIProps, ExportValue } from '../ExportDimension';
import { Box, Colors, Input, Select } from './../../../../ui';

const toHex = (color: string) => {
    if (!color) return '';
    return !!color ? (Colors.normalize(color) ?? '') : '';
};

const toRgb = (color: string) => {
    const { r, g, b } = hexToRgb(toHex(color)) ?? {};
    return r === undefined ? '' : `${r}, ${g}, ${b}`;
};

const toName = (() => {
    const cache = new Map<string, string>();
    return (color: string) => {
        if (!color) return '';
        if (cache.has(color)) return cache.get(color);
        const hex = toHex(color);
        if (!hex) return '';
        const [_, name] = ntc.name(hex) as [string, string, boolean];
        if (name) cache.set(color, name);
        return name ?? color;
    };
})();

type Formatter = (value: string) => string;
const FormatterItem = (id: string, label: string, impl: Formatter) => ({ id, label, apply: impl });
type FormatterItem = ReturnType<typeof FormatterItem>;

const formatters = [
    FormatterItem('name', 'Color Name', toName),
    FormatterItem('hex', 'Hex Code', toHex),
    FormatterItem('rgb', 'RGB', toRgb),
];

const getFormatter = (id: string) => formatters.find(x => x.id === id) ?? null;
const defaultFormatter = getFormatter('name');

const ColorColumnEditor: LFC<ExportDimensionUIProps> = ({ item, onEdit }) => {

    const selected = getFormatter(item.params.style) ?? defaultFormatter;
    const onSelect = useCallback((tm: FormatterItem) => onEdit(ExportDimension.update(item, { style: tm.id })), [item, onEdit]);

    return <Box padding="1" width="24">
        <Input.Frame>
            <Input.Top>
                <Input.Label>Format:</Input.Label>
            </Input.Top>
            <Select items={formatters} selected={selected} onChange={onSelect} />
        </Input.Frame>
    </Box>;
};

export const DefineColorColumn = <T extends Component>(
    type: ComponentType<T>, label: string, description: string, getter: (obj: T) => string) => {
    const desc = Component.describe(type);
    const id = `${desc.name}:${capitalCase(label)}`;
    return ExportDimensionType({
        id,
        label,
        description,
        ui: ColorColumnEditor,
        createInstance(params) {
            return ExportDimension({
                id,
                type: this,
                params: params ?? {},
                label,
                read(rows) {
                    const format = getFormatter(this.params.style) ?? defaultFormatter;
                    const head = [ExportValue(this.id, this.label)];
                    const body = rows.map(c => {
                        const ptpt = c.is(type) ? getter(c as T) : null;
                        return ExportValue(id, format.apply(ptpt));
                    });
                    return [head.concat(body)];
                }
            });
        }
    });
};
